import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface NewCategoryDialogProps {
  open: boolean;
  onClose: () => void;
  onAdd: (name: string, parentId?: string) => Promise<void>;
  parentOptions?: Array<{ id: string; name: string }>;
  initialParentId?: string;
}

export function NewCategoryDialog({ open, onClose, onAdd, parentOptions = [], initialParentId = '' }: NewCategoryDialogProps) {
  const { t } = useTranslation();
  const [categoryName, setCategoryName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [parentId, setParentId] = useState(initialParentId);

  useEffect(() => {
    if (open) {
      setCategoryName('');
      setParentId(initialParentId);
    }
  }, [open, initialParentId]);

  const handleAdd = async () => {
    if (!categoryName.trim()) return;
    setIsSubmitting(true);
    try {
      await onAdd(categoryName.trim(), parentId);
      setCategoryName('');
      onClose();
    } catch (error) {
      console.error('Failed to add category:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{t('entity_list.codex.tabs.new_category')}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label={t('common.name')}
          fullWidth
          variant="outlined"
          value={categoryName}
          onChange={(e) => setCategoryName(e.target.value)}
          placeholder={t('entity_list.codex.tabs.category_name_placeholder')}
          disabled={isSubmitting}
        />
        <TextField
          select
          margin="dense"
          label={t('entity_list.codex.tabs.parent_category_optional')}
          fullWidth
          value={parentId}
          onChange={(e) => setParentId(e.target.value)}
          disabled={isSubmitting}
        >
          <MenuItem value="">{t('entity_list.codex.tabs.none_top_level')}</MenuItem>
          {parentOptions.map((option) => <MenuItem key={option.id} value={option.id}>{option.name}</MenuItem>)}
        </TextField>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleAdd}
          variant="contained"
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {t('common.add')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
